// src/components/modules/LocalHistoricalVisibility.tsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { supabase } from "../../lib/supabase";
import Tooltip from "../Tooltip";
import RunComparisonBar from "../RunComparisonBar";
import { formatLocalIntentLabel } from "../../lib/localIntentLabels";
import { assessHistoricalCohort, canonicalIntentKey, compareIntentGroups } from "../../lib/localHistoricalComparison";
import {
  calculateDelta,
  getLatestRun,
  type RunHistoryItem,
} from "../../lib/runHistory";
import { getEarlierLocalRun, getEarlierLocalRuns, getLocalReportingPeriod } from "../../lib/localReportingPeriods";
import { formatPercentage } from "../../lib/percentageFormatting";

type LocalHistoricalVisibilityProps = {
  account: string;
  customerBrand?: string | null;
  selectedRunId?: string | null;
  onComparisonUpdate?: (comparison: LocalHistoryComparisonUpdate) => void;
};

export type LocalHistoryComparisonUpdate = {
  accountId: string;
  currentRunId: string;
  comparisonRunId: string | null;
  state: "loading" | "baseline" | "comparable" | "incompatible" | "insufficient" | "incomplete" | "error";
  message: string;
  scoreDelta: number | null;
  improved: number;
  decreased: number;
  flat: number;
  comparableCount: number;
};

type PromptResultRow = {
  run_id: string;
  prompt_id: string;
  category: string | null;
  prompt_type: string | null;
  prompt_weight: number | string | null;
  prompt_score: number | null;
  visibility_signal: string | null;
  brand_mentions: string[] | null;
  status: string;
  llm_provider: string | null;
  model_version: string | null;
};

type CategorySummary = {
  category: string;
  prompts: number;
  avgScore: number | null;
  strongSignals: number;
  weakSignals: number;
  absentSignals: number;
  mentionRate: number | null;
};

type RunSummary = {
  totalPrompts: number;
  directMentionPrompts: number;
  scoredPromptCount: number;
  avgScore: number | null;
  mentionRate: number | null;
  strongSignalRate: number | null;
  strongSignals: number;
  weakSignals: number;
  absentSignals: number;
  categories: CategorySummary[];
};

function toNullableNumber(value: unknown): number | null {
  if (value === null || value === undefined || value === "") return null;
  if (typeof value === "string" && value.trim() === "") return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function formatSelectedLocalRunDate(runDate: string | null): string {
  if (!runDate) return "unavailable";
  const dateOnly = runDate.slice(0, 10);
  const date = new Date(`${dateOnly}T12:00:00`);
  return Number.isNaN(date.getTime()) ? dateOnly : date.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}

function getCategoryKey(category?: string | null): string {
  return canonicalIntentKey(category);
}

function normalizeCategory(category?: string | null): string {
  return formatLocalIntentLabel(category);
}

function comparisonStateLabel(state: string): string {
  if (state === "incompatible") return "Not comparable";
  if (state === "incomplete") return "Incomplete run";
  if (state === "loading") return "Checking";
  if (state === "error") return "Unavailable";
  if (state === "baseline") return "Baseline captured";
  return "Insufficient evidence";
}

export function summarizeRun(rows: PromptResultRow[], customerBrand?: string | null): RunSummary {
  const totalPrompts = rows.length;

  const validScores = rows.map((row) => toNullableNumber(row.prompt_score)).filter((score): score is number => score !== null);
  const avgScore = validScores.length > 0
    ? validScores.reduce((sum, score) => sum + score, 0) / validScores.length
    : null;

  const strongSignals = rows.filter((row) => row.visibility_signal === "strong").length;
  const weakSignals = rows.filter((row) => row.visibility_signal === "weak").length;
  const absentSignals = rows.filter((row) => row.visibility_signal === "absent").length;

  const brandMentionedPrompts = rows.filter((row) => {
    if (!customerBrand) return row.brand_mentions && row.brand_mentions.length > 0;

    return (row.brand_mentions || []).some(
      (brand) => brand.toLowerCase() === customerBrand.toLowerCase()
    );
  }).length;

  const mentionRate = totalPrompts > 0 ? (brandMentionedPrompts / totalPrompts) * 100 : null;

  const strongSignalRate =
    totalPrompts > 0 ? (strongSignals / totalPrompts) * 100 : null;

  const byCategory = new Map<string, PromptResultRow[]>();

  rows.forEach((row) => {
    const key = getCategoryKey(row.category);
    const existing = byCategory.get(key) || [];
    existing.push(row);
    byCategory.set(key, existing);
  });

  const categories: CategorySummary[] = Array.from(byCategory.entries())
    .map(([category, categoryRows]) => {
      const prompts = categoryRows.length;
      const categoryBrandMentions = categoryRows.filter((row) => {
        if (!customerBrand) return row.brand_mentions && row.brand_mentions.length > 0;

        return (row.brand_mentions || []).some(
          (brand) => brand.toLowerCase() === customerBrand.toLowerCase()
        );
      }).length;

      const scores = categoryRows.map((row) => toNullableNumber(row.prompt_score)).filter((score): score is number => score !== null);

      return {
        category,
        prompts,
        avgScore: scores.length > 0
          ? scores.reduce((sum, score) => sum + score, 0) / scores.length
          : null,
        strongSignals: categoryRows.filter((row) => row.visibility_signal === "strong")
          .length,
        weakSignals: categoryRows.filter((row) => row.visibility_signal === "weak")
          .length,
        absentSignals: categoryRows.filter((row) => row.visibility_signal === "absent")
          .length,
        mentionRate: prompts > 0 ? (categoryBrandMentions / prompts) * 100 : 0,
      };
    })
    .sort((a, b) => (b.avgScore ?? -Infinity) - (a.avgScore ?? -Infinity));

  return {
    totalPrompts,
    directMentionPrompts: brandMentionedPrompts,
    scoredPromptCount: validScores.length,
    avgScore,
    mentionRate,
    strongSignalRate,
    strongSignals,
    weakSignals,
    absentSignals,
    categories,
  };
}

export default function LocalHistoricalVisibility({
  account,
  customerBrand,
  selectedRunId = null,
  onComparisonUpdate,
}: LocalHistoricalVisibilityProps) {
  const selectedRunIdRef = useRef(selectedRunId);
  selectedRunIdRef.current = selectedRunId;
  const [runs, setRuns] = useState<RunHistoryItem[]>([]);
  const [currentRun, setCurrentRun] = useState<RunHistoryItem | null>(null);
  const [comparisonRun, setComparisonRun] = useState<RunHistoryItem | null>(null);
  const [currentRows, setCurrentRows] = useState<PromptResultRow[]>([]);
  const [comparisonRows, setComparisonRows] = useState<PromptResultRow[]>([]);
  const [currentRowsScope, setCurrentRowsScope] = useState<string | null>(null);
  const [comparisonRowsScope, setComparisonRowsScope] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [runsError, setRunsError] = useState(false);
  const [currentResultsError, setCurrentResultsError] = useState(false);
  const [comparisonResultsError, setComparisonResultsError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    async function loadRuns() {
      setRuns([]);
      setCurrentRun(null);
      setComparisonRun(null);
      setCurrentRows([]);
      setComparisonRows([]);
      setCurrentRowsScope(null);
      setComparisonRowsScope(null);
      setRunsError(false);
      setCurrentResultsError(false);
      setComparisonResultsError(false);
      if (!account) {
        setLoading(false);
        return;
      }

      setLoading(true);

      const { data, error } = await supabase
        .from("runs")
        .select(
          "run_id, run_date, completed_at, started_at, run_label, environment, status, prompt_count"
        )
        .eq("account_id", account)
        .eq("status", "completed")
        .order("completed_at", { ascending: false, nullsFirst: false })
          .order("run_date", { ascending: false, nullsFirst: false })
        .limit(12);

      if (error) {
        console.error("[LocalHistoricalVisibility] Runs query error", error);
        if (!cancelled) {
          setRunsError(true);
          setLoading(false);
        }
        return;
      }

      const runItems = (data || []) as RunHistoryItem[];
      const current = getLocalReportingPeriod(runItems, selectedRunIdRef.current) || getLatestRun(runItems);
      const previous = getEarlierLocalRun(runItems, current);

      if (!cancelled) {
        setRunsError(false);
        setRuns(runItems);
        setCurrentRun(current);
        setComparisonRun(previous);
        setLoading(false);
      }
    }

    loadRuns();
    return () => { cancelled = true; };
  }, [account]);

  useEffect(() => {
    if (!runs.length) return;
    const selected = getLocalReportingPeriod(runs, selectedRunId);
    if (!selected) return;
    setCurrentRun((current) => current?.run_id === selected.run_id ? current : selected);
    setComparisonRun(getEarlierLocalRun(runs, selected));
  }, [runs, selectedRunId]);

  const activeCurrentRun = selectedRunId
    ? runs.find((run) => run.run_id === selectedRunId) || (currentRun?.run_id === selectedRunId ? currentRun : null)
    : currentRun;
  const eligibleComparisonRuns = getEarlierLocalRuns(runs, activeCurrentRun);
  const activeComparisonRun = eligibleComparisonRuns.find((run) => run.run_id === comparisonRun?.run_id)
    || getEarlierLocalRun(runs, activeCurrentRun);

  useEffect(() => {
    let cancelled = false;
    async function loadResults() {
      if (!account || !activeCurrentRun?.run_id) {
        setCurrentRows([]);
        setCurrentRowsScope(null);
        setCurrentResultsError(false);
        return;
      }

      const { data, error } = await supabase
        .from("prompt_results")
        .select("run_id, prompt_id, category, prompt_type, prompt_weight, status, llm_provider, model_version, prompt_score, visibility_signal, brand_mentions")
        .eq("account_id", account)
        .eq("run_id", activeCurrentRun.run_id);

      if (error) {
        console.error("[LocalHistoricalVisibility] Current results query error", error);
        if (!cancelled) {
          setCurrentRows([]);
          setCurrentRowsScope(null);
          setCurrentResultsError(true);
        }
        return;
      }

      if (!cancelled) {
        setCurrentRows((data || []) as PromptResultRow[]);
        setCurrentRowsScope(`${account}:${activeCurrentRun.run_id}`);
        setCurrentResultsError(false);
      }
    }

    setCurrentRows([]);
    setCurrentRowsScope(null);
    setCurrentResultsError(false);
    loadResults();
    return () => { cancelled = true; };
  }, [account, activeCurrentRun?.run_id]);

  useEffect(() => {
    let cancelled = false;
    async function loadComparisonResults() {
      if (!account || !activeComparisonRun?.run_id) {
        setComparisonRows([]);
        setComparisonRowsScope(null);
        setComparisonResultsError(false);
        return;
      }

      const { data, error } = await supabase
        .from("prompt_results")
        .select("run_id, prompt_id, category, prompt_type, prompt_weight, status, llm_provider, model_version, prompt_score, visibility_signal, brand_mentions")
        .eq("account_id", account)
        .eq("run_id", activeComparisonRun.run_id);

      if (error) {
        console.error("[LocalHistoricalVisibility] Comparison results query error", error);
        if (!cancelled) {
          setComparisonRows([]);
          setComparisonRowsScope(null);
          setComparisonResultsError(true);
        }
        return;
      }

      if (!cancelled) {
        setComparisonRows((data || []) as PromptResultRow[]);
        setComparisonRowsScope(`${account}:${activeComparisonRun.run_id}`);
        setComparisonResultsError(false);
      }
    }

    setComparisonRows([]);
    setComparisonRowsScope(null);
    setComparisonResultsError(false);
    loadComparisonResults();
    return () => { cancelled = true; };
  }, [account, activeComparisonRun?.run_id]);

  const currentSummary = useMemo(
    () => summarizeRun(
      currentRowsScope === `${account}:${activeCurrentRun?.run_id}` ? currentRows : [],
      customerBrand
    ),
    [currentRowsScope, currentRows, account, activeCurrentRun?.run_id, customerBrand]
  );

  const comparisonSummary = useMemo(
    () => summarizeRun(
      comparisonRowsScope === `${account}:${activeComparisonRun?.run_id}` ? comparisonRows : [],
      customerBrand
    ),
    [comparisonRowsScope, comparisonRows, account, activeComparisonRun?.run_id, customerBrand]
  );

  const cohortAssessment = useMemo(() => {
    if (!activeComparisonRun) return { state: "baseline" as const, reason: null, matchedCount: 0 };
    if (currentResultsError || comparisonResultsError) return { state: "error" as const, reason: "results-load-error", matchedCount: 0 };
    if (currentRowsScope !== `${account}:${activeCurrentRun?.run_id}` || comparisonRowsScope !== `${account}:${activeComparisonRun.run_id}`) {
      return { state: "loading" as const, reason: null, matchedCount: 0 };
    }
    return assessHistoricalCohort(currentRows, comparisonRows);
  }, [account, comparisonResultsError, comparisonRows, comparisonRowsScope, activeComparisonRun, currentResultsError, currentRows, currentRowsScope, activeCurrentRun?.run_id]);

  const scoreDelta = cohortAssessment.state !== "comparable"
    ? { label: activeComparisonRun ? comparisonStateLabel(cohortAssessment.state) : "Baseline captured", direction: "new" as const }
    : currentSummary.avgScore === null || comparisonSummary.avgScore === null
      ? { label: "Unavailable", direction: "new" as const }
    : calculateDelta(
      currentSummary.avgScore,
      comparisonSummary.avgScore,
      { decimals: 1 }
    );

  const mentionDelta = cohortAssessment.state !== "comparable"
    ? { label: activeComparisonRun ? comparisonStateLabel(cohortAssessment.state) : "Baseline captured", direction: "new" as const }
    : currentSummary.mentionRate === null || comparisonSummary.mentionRate === null
      ? { label: "Unavailable", direction: "new" as const }
      : calculateDelta(currentSummary.mentionRate, comparisonSummary.mentionRate, { suffix: " percentage points", decimals: 1 });

  const strongDelta = cohortAssessment.state !== "comparable"
    ? { label: activeComparisonRun ? comparisonStateLabel(cohortAssessment.state) : "Baseline captured", direction: "new" as const }
    : currentSummary.strongSignalRate === null || comparisonSummary.strongSignalRate === null
      ? { label: "Unavailable", direction: "new" as const }
      : calculateDelta(currentSummary.strongSignalRate, comparisonSummary.strongSignalRate, { suffix: "%", decimals: 1 });

  const intentGroupChange = useMemo(
    () => compareIntentGroups(currentSummary.categories, comparisonSummary.categories, cohortAssessment.state),
    [cohortAssessment.state, currentSummary.categories, comparisonSummary.categories]
  );

  const historyUpdate = useMemo<LocalHistoryComparisonUpdate | null>(() : LocalHistoryComparisonUpdate | null => {
    if (!activeCurrentRun?.run_id) return null;
    if (!activeComparisonRun) return {
      accountId: account, currentRunId: activeCurrentRun.run_id, comparisonRunId: null,
      state: "baseline", message: "No earlier completed run is available for comparison.",
      scoreDelta: null, improved: 0, decreased: 0, flat: 0, comparableCount: 0,
    };
    if (cohortAssessment.state !== "comparable") {
      const messages: Record<string, string> = {
        loading: "Validating the selected historical comparison…",
        incompatible: "These runs use different prompt cohorts or prompt methods, so historical changes are not comparable.",
        insufficient: "Saved prompt and scoring details are insufficient to verify this historical comparison.",
        incomplete: "One or both runs have incomplete or error results, so historical changes are unavailable.",
        error: "Historical comparison data could not be loaded. No trend is being reported.",
      };
      return {
        accountId: account, currentRunId: activeCurrentRun.run_id, comparisonRunId: activeComparisonRun.run_id,
        state: cohortAssessment.state as LocalHistoryComparisonUpdate["state"], message: messages[cohortAssessment.state] || "Historical comparison is unavailable.",
        scoreDelta: null, improved: 0, decreased: 0, flat: 0, comparableCount: 0,
      };
    }
    const groupChange = compareIntentGroups(currentSummary.categories, comparisonSummary.categories, "comparable");
    const scoreChange = currentSummary.avgScore === null || comparisonSummary.avgScore === null
      ? null
      : currentSummary.avgScore - comparisonSummary.avgScore;
    const movement = groupChange.improved > 0 && groupChange.decreased > 0
      ? "mixed historical movement"
      : groupChange.improved > 0
        ? `${groupChange.improved} of ${groupChange.comparableCount} comparable intent groups improved`
        : groupChange.decreased > 0
          ? `${groupChange.decreased} of ${groupChange.comparableCount} comparable intent groups declined`
          : groupChange.comparableCount > 0
            ? `${groupChange.flat} comparable intent groups were unchanged`
            : "no scored intent groups were available";
    const scoreText = scoreChange === null
      ? "The aggregate prompt-score change is unavailable."
      : `Average prompt score ${scoreChange > 0 ? "increased" : scoreChange < 0 ? "decreased" : "was unchanged"} by ${Math.abs(scoreChange).toFixed(1)} points out of 10.`;
    return {
      accountId: account, currentRunId: activeCurrentRun.run_id, comparisonRunId: activeComparisonRun.run_id,
      state: groupChange.comparableCount > 0 ? "comparable" : "insufficient",
      message: groupChange.comparableCount > 0 ? `${movement}. ${scoreText}` : "No scored intent groups are available for a verified comparison.",
      scoreDelta: scoreChange, improved: groupChange.improved, decreased: groupChange.decreased,
      flat: groupChange.flat, comparableCount: groupChange.comparableCount,
    };
  }, [account, cohortAssessment, activeComparisonRun, comparisonSummary.avgScore, comparisonSummary.categories, activeCurrentRun?.run_id, currentSummary.avgScore, currentSummary.categories]);

  useEffect(() => {
    if (historyUpdate) onComparisonUpdate?.(historyUpdate);
  }, [historyUpdate, onComparisonUpdate]);

  const intentGroupDirection: "up" | "down" | "flat" | "new" = intentGroupChange.state !== "comparable"
    ? "new"
    : intentGroupChange.improved > intentGroupChange.decreased
      ? "up"
      : intentGroupChange.decreased > intentGroupChange.improved
        ? "down"
        : "flat";
  const intentGroupStatus = intentGroupChange.state !== "comparable"
    ? !activeComparisonRun
      ? getMetricStatus("change", null, { isBaseline: true })
      : {
        label: "N/A",
        detail: cohortAssessment.state === "comparable" ? "No matched categories" : comparisonStateLabel(cohortAssessment.state),
        color: "#64748b", background: "#f1f5f9", border: "#cbd5e1",
      }
    : intentGroupChange.improved > 0 && intentGroupChange.decreased > 0 && intentGroupChange.improved === intentGroupChange.decreased
      ? { label: "Mixed", detail: "Gains and declines balance", color: "#64748b", background: "#f1f5f9", border: "#cbd5e1" }
      : getMetricStatus("change", intentGroupChange.improved - intentGroupChange.decreased);

  function handleComparisonRunChange(runId: string) {
    const selected = eligibleComparisonRuns.find((run) => run.run_id === runId) || null;
    setComparisonRun(selected);
  }

  if (loading) {
    return (
      <section style={styles.panel}>
        <p style={styles.eyebrow}>Historical performance</p>
        <h2 style={styles.title}>Loading local visibility history…</h2>
      </section>
    );
  }

  if (runsError) {
    return (
      <section style={styles.panel}>
        <p style={styles.eyebrow}>Historical performance</p>
        <h2 style={styles.title}>Local visibility history</h2>
        <p role="status" style={styles.muted}>Completed-run history could not be loaded. No comparison is being reported.</p>
      </section>
    );
  }

  if (!activeCurrentRun) {
    return (
      <section style={styles.panel}>
        <p style={styles.eyebrow}>Historical performance</p>
        <h2 style={styles.title}>Local visibility history</h2>
        <p style={styles.muted}>
          No completed runs are available yet. Once this account has a completed run,
          Toren will begin building a benchmark.
        </p>
      </section>
    );
  }

  return (
    <section style={styles.panel}>
      <div style={styles.header}>
        <div>
          <p style={styles.eyebrow}>Historical performance</p>
          <h2 style={styles.title}>Local visibility history</h2>
          <p data-testid="local-history-selected-run" style={{ ...styles.subtitle, marginTop: 4, fontWeight: 700 }}>
            Reporting period: {formatSelectedLocalRunDate(activeCurrentRun.run_date)}
          </p>
          <p style={styles.subtitle}>
            Compare recorded Local prompt scores, direct mentions, and signal rates across completed runs.
          </p>
        </div>
      </div>

      <RunComparisonBar
        currentRun={activeCurrentRun}
        comparisonRun={activeComparisonRun}
        availableRuns={eligibleComparisonRuns}
        onComparisonRunChange={handleComparisonRunChange}
      />
      {currentResultsError && <p role="status" style={styles.promptCountNotice}>Current-run measurements could not be loaded. Score and rate values are unavailable.</p>}
      {comparisonResultsError && <p role="status" style={styles.promptCountNotice}>Selected comparison measurements could not be loaded. Comparison deltas are unavailable.</p>}
      {activeComparisonRun && cohortAssessment.state !== "comparable" && (
        <div role="status" style={styles.promptCountNotice}>{historyUpdate?.message}</div>
      )}


      {!activeComparisonRun && (
        <div style={styles.baselineNotice}>
          <strong>Baseline captured.</strong> No earlier completed run is available for this
          reporting period, so no historical movement is reported.
        </div>
      )}

      <div style={styles.metricGrid}>
        <MetricCard
          label={
            <>
              Average Prompt Score{" "}
              <Tooltip text="Average prompt_score across this run's Local prompt results, displayed on the existing 0–10 scale." />
            </>
          }
          value={formatScoreWithContext(currentSummary.avgScore)}
          delta={
            activeComparisonRun
              ? cohortAssessment.state === "comparable" ? scoreDelta.label : comparisonStateLabel(cohortAssessment.state)
              : currentSummary.avgScore === null
                ? "Unavailable"
                : `${getMetricStatus("score", currentSummary.avgScore).label} · Baseline captured`
          }
          direction={
            currentSummary.avgScore === null
              ? "new"
              : currentSummary.avgScore < 3
              ? "down"
              : currentSummary.avgScore < 7
              ? "flat"
              : "up"
          }
          status={getMetricStatus("score", currentSummary.avgScore, { isUnavailable: currentSummary.avgScore === null })}
          detail={currentSummary.scoredPromptCount > 0 ? `${currentSummary.scoredPromptCount} of ${currentSummary.totalPrompts} prompt results have a prompt score` : "Prompt scores unavailable for this run"}
        />
        <MetricCard
          label={
            <>
              Direct Brand Mention Rate{" "}
              <Tooltip text="The percentage of this run's prompt results whose brand_mentions includes the account's business name." />
            </>
          }
          value={formatRateWithContext(currentSummary.mentionRate)}
          delta={
            activeComparisonRun
              ? mentionDelta.label
              : currentSummary.mentionRate === null
                ? "Unavailable"
                : `${getMetricStatus("rate", currentSummary.mentionRate).label} direct recall · Baseline captured`
          }
          direction={
            currentSummary.mentionRate === null
              ? "new"
              : currentSummary.mentionRate < 10
              ? "down"
              : currentSummary.mentionRate < 35
              ? "flat"
              : "up"
          }
          status={getMetricStatus("rate", currentSummary.mentionRate, { isUnavailable: currentSummary.mentionRate === null })}
          detail={currentSummary.totalPrompts > 0 ? `${currentSummary.directMentionPrompts} of ${currentSummary.totalPrompts} prompt results include the business name` : "Direct mention count unavailable"}
        />
        <MetricCard
          label={
            <>
              Strong Signal Rate{" "}
              <Tooltip text="The percentage of this run's prompt results whose visibility_signal is strong." />
            </>
          }
          value={formatRateWithContext(currentSummary.strongSignalRate)}
          delta={
            activeComparisonRun
              ? strongDelta.label
              : currentSummary.strongSignalRate === null
                ? "Unavailable"
                : `${getMetricStatus("rate", currentSummary.strongSignalRate).label} strong visibility signals · Baseline captured`
          }
          direction={
            currentSummary.strongSignalRate === null
              ? "new"
              : currentSummary.strongSignalRate < 10
              ? "down"
              : currentSummary.strongSignalRate < 35
              ? "flat"
              : "up"
          }
          status={getMetricStatus("rate", currentSummary.strongSignalRate, { isUnavailable: currentSummary.strongSignalRate === null })}
          detail={currentSummary.totalPrompts > 0 ? `${currentSummary.strongSignals} of ${currentSummary.totalPrompts} prompt results have a strong visibility signal` : "Strong-signal count unavailable"}
        />
        <MetricCard
           label={
             <>
               Intent Group Change{" "}
               <Tooltip text="Shows how many local search intent groups improved, declined, or stayed flat compared with the selected comparison run." />
             </>
           }
           value={intentGroupChange.state === "comparable" ? `${intentGroupChange.improved} improved` : "N/A"}
           delta={
           !activeComparisonRun
           ? "Toren is still learning · Needs another completed run"
           : cohortAssessment.state !== "comparable"
             ? cohortAssessment.state === "incompatible" ? "Different prompt methods" : comparisonStateLabel(cohortAssessment.state)
             : intentGroupChange.state === "unavailable"
               ? "No comparable intent groups"
               : `${intentGroupChange.decreased} decreased · ${intentGroupChange.flat} flat · ${intentGroupChange.comparableCount} of ${intentGroupChange.currentCount} comparable`
         }
         direction={activeComparisonRun ? intentGroupDirection : "flat"}
          status={activeComparisonRun ? intentGroupStatus : getMetricStatus("change", null, { isUnavailable: true })}
        />
      </div>

      <div style={styles.tableWrapper}>
        <div style={styles.tableHeader}>
            <h3 style={styles.tableTitle}>Historical measurements by Local prompt category</h3>
          <p style={styles.tableSubtitle}>
            Category movement appears only for exact intent identifiers with valid scores in both selected runs.
          </p>
        </div>

        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>Intent group</th>
              <th style={styles.thRight}>Average prompt score (/10)</th>
              <th style={styles.thRight}>Previous score</th>
              <th style={styles.thRight}>Change</th>
              <th style={styles.thRight}>Direct mention rate</th>
            </tr>
          </thead>
          <tbody>
            {currentSummary.categories.map((category) => {
              const previousCategory = comparisonSummary.categories.find(
                (item) => getCategoryKey(item.category) === getCategoryKey(category.category)
              );
              const hasComparableCategory = Boolean(activeComparisonRun && previousCategory && category.avgScore !== null && previousCategory.avgScore !== null);

              const delta = category.avgScore === null
                ? { label: "Unavailable", direction: "new" as const }
                : calculateDelta(
                  category.avgScore,
                  hasComparableCategory ? previousCategory?.avgScore : null,
                  { decimals: 1 }
                );

              return (
                <tr key={category.category}>
                  <td style={styles.td}>{normalizeCategory(category.category)}</td>
                  <td style={styles.tdRight}>{formatScoreWithContext(category.avgScore)}</td>
                  <td style={styles.tdRight}>
                    {previousCategory?.avgScore == null ? "—" : previousCategory.avgScore.toFixed(1)}
                  </td>
                  <td style={styles.tdRight}>
  <span style={getDeltaStyle(delta.direction)}>
            {activeComparisonRun && cohortAssessment.state !== "comparable" ? comparisonStateLabel(cohortAssessment.state) : activeComparisonRun && !hasComparableCategory ? "Not comparable" : delta.label}
  </span>
</td>
                  <td style={styles.tdRight}>{category.mentionRate.toFixed(1)}%</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function MetricCard({
  label,
  value,
  delta,
  direction,
  status,
  detail,
}: {
  label: React.ReactNode;
  value: string;
  delta: string;
  direction: "up" | "down" | "flat" | "new";
  status?: {
    label: string;
    detail: string;
    color: string;
    background: string;
    border: string;
  };
  detail?: string;
}) {
  return (
    <div style={styles.metricCard}>
      <p style={styles.metricLabel}>{label}</p>
      <div style={styles.metricValue}>{value}</div>
      {detail && <p style={{ ...styles.muted, margin: "6px 0 0", fontSize: 12 }}>{detail}</p>}
      {status && (
        <div
          style={{
            ...styles.metricStatusPill,
            color: status.color,
            background: status.background,
            borderColor: status.border,
          }}
        >
          {status.label} · {status.detail}
        </div>
      )}
      <p style={getDeltaStyle(direction)}>{delta}</p>
    </div>
  );
}

function getDeltaStyle(direction: "up" | "down" | "flat" | "new"): React.CSSProperties {
  if (direction === "up") return styles.deltaUp;
  if (direction === "down") return styles.deltaDown;
  if (direction === "flat") return styles.deltaFlat;
  return styles.deltaNew;
}


const getMetricStatus = (
  metricType: "score" | "rate" | "change",
  value: number | null | undefined,
  options?: { isBaseline?: boolean; isUnavailable?: boolean }
) => {
  if (options?.isUnavailable || value === null || value === undefined || Number.isNaN(Number(value))) {
    return {
      label: "N/A",
      detail: "Toren is still learning",
      color: "#64748b",
      background: "#f1f5f9",
      border: "#cbd5e1",
    };
  }

  if (options?.isBaseline) {
    return {
      label: "Baseline",
      detail: "Awaiting comparison run",
      color: "#2563eb",
      background: "#eff6ff",
      border: "#bfdbfe",
    };
  }

  const numericValue = Number(value);

  if (metricType === "score") {
    if (numericValue < 3) {
      return {
        label: "Low",
        detail: "Lower average Local prompt score",
        color: "#b91c1c",
        background: "#fef2f2",
        border: "#fecaca",
      };
    }
    if (numericValue < 7) {
      return {
        label: "Moderate",
        detail: "Mid-range average Local prompt score",
        color: "#b45309",
        background: "#fffbeb",
        border: "#fde68a",
      };
    }
    return {
      label: "High",
      detail: "Higher average Local prompt score",
      color: "#047857",
      background: "#ecfdf5",
      border: "#a7f3d0",
    };
  }

  if (metricType === "rate") {
    if (numericValue < 10) {
      return {
        label: "Low",
        detail: "Limited signal",
        color: "#b91c1c",
        background: "#fef2f2",
        border: "#fecaca",
      };
    }
    if (numericValue < 35) {
      return {
        label: "Developing",
        detail: "Signal emerging",
        color: "#b45309",
        background: "#fffbeb",
        border: "#fde68a",
      };
    }
    return {
      label: "Strong",
      detail: "Clear signal",
      color: "#047857",
      background: "#ecfdf5",
      border: "#a7f3d0",
    };
  }

  return {
    label: numericValue > 0 ? "Improving" : numericValue < 0 ? "Declining" : "Flat",
    detail: numericValue > 0 ? "Positive movement" : numericValue < 0 ? "Lost ground" : "No movement yet",
    color: numericValue > 0 ? "#047857" : numericValue < 0 ? "#b91c1c" : "#64748b",
    background: numericValue > 0 ? "#ecfdf5" : numericValue < 0 ? "#fef2f2" : "#f1f5f9",
    border: numericValue > 0 ? "#a7f3d0" : numericValue < 0 ? "#fecaca" : "#cbd5e1",
  };
};

const formatScoreWithContext = (value: number | null | undefined) => {
  if (value === null || value === undefined || Number.isNaN(Number(value))) return "N/A";
  return `${Number(value).toFixed(1)} / 10`;
};

const formatRateWithContext = (value: number | null | undefined) => {
  return formatPercentage(value, { unavailable: "N/A" });
};

const styles: Record<string, React.CSSProperties> = {
  panel: {
    border: "1px solid #e5e7eb",
    borderRadius: 20,
    padding: 20,
    background: "#f8fafc",
    marginBottom: 24,
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    gap: 16,
    marginBottom: 16,
  },
  eyebrow: {
    margin: 0,
    color: "#64748b",
    fontSize: 12,
    fontWeight: 800,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
  },
  title: {
    margin: "4px 0 0",
    fontSize: 22,
    color: "#0f172a",
  },
  subtitle: {
    margin: "6px 0 0",
    color: "#475569",
    fontSize: 14,
  },
  muted: {
    color: "#64748b",
    fontSize: 14,
  },
  baselineNotice: {
    border: "1px solid #bfdbfe",
    background: "#eff6ff",
    color: "#1e3a8a",
    borderRadius: 14,
    padding: 14,
    fontSize: 14,
    marginBottom: 16,
  },
  promptCountNotice: {
  border: "1px solid #fde68a",
  background: "#fffbeb",
  color: "#92400e",
  borderRadius: 14,
  padding: 12,
  fontSize: 13,
  fontWeight: 700,
  marginBottom: 16,
},
  metricGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
    gap: 12,
    marginBottom: 18,
  },
  metricCard: {
    background: "#ffffff",
    border: "1px solid #e2e8f0",
    borderRadius: 16,
    padding: 16,
  },
  metricLabel: {
    margin: 0,
    color: "#64748b",
    fontSize: 13,
    fontWeight: 700,
  },
  metricValue: {
    marginTop: 8,
    fontSize: 28,
    fontWeight: 900,
    color: "#0f172a",
  },
  metricStatusPill: {
    display: "inline-flex",
    alignItems: "center",
    marginTop: 8,
    padding: "4px 9px",
    borderRadius: 999,
    fontSize: 12,
    fontWeight: 800,
    border: "1px solid",
  },
  deltaUp: {
    margin: "8px 0 0",
    color: "#15803d",
    fontSize: 13,
    fontWeight: 800,
  },
  deltaDown: {
    margin: "8px 0 0",
    color: "#b91c1c",
    fontSize: 13,
    fontWeight: 800,
  },
  deltaFlat: {
    margin: "8px 0 0",
    color: "#475569",
    fontSize: 13,
    fontWeight: 800,
  },
  deltaNew: {
    margin: "8px 0 0",
    color: "#2563eb",
    fontSize: 13,
    fontWeight: 800,
  },
  tableWrapper: {
    background: "#ffffff",
    border: "1px solid #e2e8f0",
    borderRadius: 16,
    overflow: "hidden",
  },
  tableHeader: {
    padding: 16,
    borderBottom: "1px solid #e2e8f0",
  },
  tableTitle: {
    margin: 0,
    fontSize: 16,
    color: "#0f172a",
  },
  tableSubtitle: {
    margin: "4px 0 0",
    fontSize: 13,
    color: "#64748b",
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
    fontSize: 13,
  },
  th: {
    textAlign: "left",
    padding: "10px 14px",
    color: "#64748b",
    borderBottom: "1px solid #e2e8f0",
    background: "#f8fafc",
  },
  thRight: {
    textAlign: "right",
    padding: "10px 14px",
    color: "#64748b",
    borderBottom: "1px solid #e2e8f0",
    background: "#f8fafc",
  },
  td: {
    padding: "12px 14px",
    borderBottom: "1px solid #f1f5f9",
    color: "#0f172a",
    fontWeight: 700,
  },
  tdRight: {
    padding: "12px 14px",
    borderBottom: "1px solid #f1f5f9",
    color: "#0f172a",
    textAlign: "right",
  },
};
